import React, { useState, useEffect } from 'react';

import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

// Types
import { SearchSuggestion } from '@src/types/search';

interface SearchSuggestionsProps {
  title?: string;
  suggestions?: SearchSuggestion[];
  className?: string;
  settings: any;
  onSuggestionClick?: (suggestion: string) => void;
}

export const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  title = 'Search suggestions',
  className = '',
  settings,
  onSuggestionClick
}) => {
  const { isMobile } = useResponsiveBreakpoints();
  const [suggestions, setSuggestions] = useState<string[] | null>(null);

  const renderLimit = settings.max_results || 7;
  // Generate spacing classes from settings
  const spacingClasses = [
    settings.margin_top ? `mt-[${settings.margin_top}px]` : '',
    settings.margin_bottom ? `mb-[${settings.margin_bottom}px]` : '',
    settings.margin_left ? `ml-[${settings.margin_left}px]` : '',
    settings.margin_right ? `mr-[${settings.margin_right}px]` : '',
    settings.padding_top ? `pt-[${settings.padding_top}px]` : '',
    settings.padding_bottom ? `pb-[${settings.padding_bottom}px]` : '',
    settings.padding_left ? `pl-[${settings.padding_left}px]` : '',
    settings.padding_right ? `pr-[${settings.padding_right}px]` : ''
  ]
    .filter(Boolean)
    .join(' ');

  const handleClickSuggestion = (suggestion: string) => {
    if (onSuggestionClick) {
      onSuggestionClick(suggestion);
    } else {
      // Default behavior: redirect to search results page with the suggestion as query
      const searchUrl = `/search?q=${encodeURIComponent(suggestion)}`;
      window.location.href = searchUrl;
    }
  };

  const handleSuggestionsUpdate = (event: Event) => {
    const newSuggestions = (event as CustomEvent).detail.suggestions as SearchSuggestion[];

    if (!newSuggestions) return;

    setSuggestions(newSuggestions);
  };

  const handleSearchStateChange = (event: Event) => {
    const { hasResults }: { hasResults: boolean; query: string } = (event as CustomEvent).detail || {};

    if (!hasResults) {
      setSuggestions([]);
    }
  };

  useEffect(() => {
    // Listen for suggestions data from search results
    window.addEventListener('searchrequest-suggestions-complete', handleSuggestionsUpdate);
    window.addEventListener('search-state-changed', handleSearchStateChange);

    return () => {
      window.removeEventListener('searchrequest-suggestions-complete', handleSuggestionsUpdate);
      window.removeEventListener('search-state-changed', handleSearchStateChange);
    };
  }, []);

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div className={`w-full ${spacingClasses} ${className}`}>
      <p className={`mb-4 ${isMobile ? 'b7--bold' : 'b6--bold'}`}>{title}</p>

      <div className='flex flex-wrap lg:flex-col max-md:gap-y-2 gap-4 items-start'>
        {suggestions.slice(0, renderLimit).map((suggestion, index) => (
          <button
            key={index}
            onClick={() => handleClickSuggestion(suggestion.text || suggestion)}
            className={`block hover:underline w-auto ${isMobile ? 'b8--medium' : 'b7--medium'}`}
            title={`View more search results for ${suggestion.text || suggestion}`}
          >
            <span dangerouslySetInnerHTML={{ __html: suggestion.text || suggestion }}></span>
          </button>
        ))}
      </div>
    </div>
  );
};
